import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  Fab,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  type ClassesType,
  type CustomFieldValidatorState,
  ErrorTooltip,
  LanguageUtils,
  type Nil,
  PermissionArea,
  PuiDialog,
  PuiPopper,
  type Question,
  type QuestionAnswer,
  Shadow,
  TextWithTooltip,
  useFields,
} from '@pbt/pbt-ui-components'

import type { AnswerInputType } from '~/api/graphql/generated/types'
import PreviewButton from '~/components/common/buttons/PreviewButton'
import FeatureToggle from '~/constants/featureToggle'
import { getCRUDByArea } from '~/store/reducers/auth'
import {
  getAnswerInputTypes,
  getFeatureToggle,
} from '~/store/reducers/constants'
import type { DataHandleWithUnsavedChanges } from '~/types'
import { isFieldValuesChanged } from '~/utils'
import useFieldsChanged from '~/utils/useFieldsChanged'

import AnswerPopup from './AnswerPopup'
import ConfirmAnswerSwitchPopup from './ConfirmAnswerSwitchPopup'
import { QuestionContentFields } from './QuestionContentFields'
import QuestionInput from './QuestionInput'
import QuestionPreviewContainer from './QuestionPreviewContainer'
import { getAnswerTypeFlags } from './questionUtils'

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    button: {
      width: 150,
      height: 40,
    },
    radioLabel: {
      fontSize: '1.6rem',
      color: theme.colors.grayGray1,
      marginLeft: theme.spacing(0.5),
    },
    radio: {
      padding: theme.spacing(0.5),
    },
    labelRoot: {
      margin: 0,
    },
  }),
  { name: 'Question' },
)

export interface QuestionHandle extends DataHandleWithUnsavedChanges {
  proceed: () => void
}

interface QuestionProps {
  classes?: ClassesType<typeof useStyles>
  disableAnswerEdit?: boolean
  disabled?: boolean
  onChange?: () => void
  onOk?: (newQuestion: Question) => void
  question?: Question
  view?: boolean
}

const Question = forwardRef<QuestionHandle, QuestionProps>(function Question(
  {
    classes: classesProp,
    question: questionProp,
    view,
    onOk,
    disabled,
    onChange = R.F,
    disableAnswerEdit = false,
  },
  ref,
) {
  const classes = useStyles({ classes: classesProp })
  const AnswerInputTypes: AnswerInputType[] = useSelector(getAnswerInputTypes)
  const permissions = useSelector(getCRUDByArea(PermissionArea.BUSINESS))
  const { t } = useTranslation(['Admin', 'Common', 'Tooltips'])

  const isCreate = !questionProp

  const [addAnswerPopupOpen, setAddAnswerPopupOpen] = useState(false)
  const [previewPopupOpen, setPreviewPopupOpen] = useState(false)
  const [confirmAnswerSwitchPopupOpen, setConfirmAnswerSwitchPopupOpen] =
    useState(false)
  const [questionCandidate, setQuestionCandidate] = useState<
    Partial<Question> | Nil
  >(questionProp)
  const [answerToEdit, setAnswerToEdit] = useState<QuestionAnswer | Nil>(null)
  const [pendingChange, setPendingChange] = useState<string | Nil>(null)

  const rootRef = useRef<HTMLDivElement>(null)
  const answersLength = questionCandidate?.answers?.length || 0
  const disableAnswer = disabled || !permissions.update || disableAnswerEdit
  const isQuestionsDynamicTextAndSubtextEnabled = useSelector(
    getFeatureToggle(FeatureToggle.QUESTIONS_DYNAMIC_TEXT_AND_SUBTEXT),
  )

  const validateAnswerType = ({
    state: { answer },
  }: CustomFieldValidatorState) => {
    const { isMultipleSelectionType } = getAnswerTypeFlags(
      answer,
      AnswerInputTypes,
    )
    return !isMultipleSelectionType || answersLength > 1
  }

  const { fields, validate, reset } = useFields(
    [
      {
        name: 'questionText',
        label: t('Admin:CATALOG.QUESTION.QUESTION_TEXT_FIELD'),
        validators: ['required'],
        initialValue: isCreate ? '' : questionCandidate?.questionText,
      },
      ...(isQuestionsDynamicTextAndSubtextEnabled
        ? [
            {
              name: 'questionSubText',
              label: t('Admin:CATALOG.QUESTION.QUESTION_SUBTEXT_FIELD'),
              initialValue: isCreate ? '' : questionCandidate?.questionSubText,
            },
          ]
        : []),
      {
        name: 'answer',
        label: t('Common:ANSWER_ONE'),
        validators: [
          'required',
          { validator: validateAnswerType, validatorName: 'answersRequired' },
        ],
        initialValue: isCreate ? '' : questionCandidate?.answerInputTypeId,
      },
    ],
    false,
  )

  const { questionText, answer, questionSubText } = fields

  useFieldsChanged(() => {
    onChange()
  }, fields)

  useEffect(() => {
    setQuestionCandidate(questionProp)
    if (questionProp) {
      reset()
    }
  }, [questionProp])

  const { isQuantitySelectorType, isMultipleSelectionType } =
    getAnswerTypeFlags(answer.value, AnswerInputTypes)

  const hasQuestionText = Boolean(questionCandidate?.questionText)
  const hasAnswers = Boolean(questionCandidate?.answers)
  const hasMinimumAnswersForMultipleSelection =
    isMultipleSelectionType && answersLength > 1
  const hasMinimumAnswersForSingleSelection =
    !isMultipleSelectionType && answersLength > 0
  const hasValidAnswerConfiguration =
    hasAnswers &&
    (hasMinimumAnswersForMultipleSelection ||
      hasMinimumAnswersForSingleSelection)

  const showPreview =
    hasQuestionText && (isQuantitySelectorType || hasValidAnswerConfiguration)

  const hasMultipleSelectionAnswers = Boolean(
    isMultipleSelectionType && questionCandidate?.answers && answersLength > 0,
  )
  useEffect(() => {
    setQuestionCandidate({
      ...(questionCandidate || {}),
      questionText: questionText.value,
      questionSubText: questionSubText?.value,
      active: true,
    })
  }, [questionText.value, questionSubText?.value])

  useEffect(() => {
    if (questionCandidate?.answers) {
      onChange()
    }
  }, [questionCandidate?.answers, questionCandidate?.answerInputSubtypeId])

  const updateQuestionCandidate = (value: string) => {
    const { isMultipleSelectionType: isNewMultipleSelectionType } =
      getAnswerTypeFlags(value, AnswerInputTypes)

    const newAnswers = isNewMultipleSelectionType
      ? []
      : [
          {
            inputs: [
              {
                inputTypeId: value,
              },
            ],
          },
        ]

    setQuestionCandidate({
      ...(questionCandidate || {}),
      answers: newAnswers as QuestionAnswer[],
      answerInputTypeId: value,
    })
  }

  const createQuestion = () => ({
    ...(questionCandidate || {}),
    questionText: questionText.value,
    questionSubText: questionSubText?.value || null,
  })

  const next = () => {
    if (onOk && validate()) {
      onOk(createQuestion() as Question)
    }
  }

  useImperativeHandle(ref, () => ({
    validate,
    get: createQuestion,
    hasUnsavedChanges: () => isFieldValuesChanged(fields),
    proceed: next,
  }))

  const openPreview = () => {
    if (validate()) {
      setPreviewPopupOpen(true)
    }
  }

  const onAddAnswerRequested = () => {
    setAddAnswerPopupOpen(true)
  }

  const onAnswerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    validate({ silent: true })
    if (hasMultipleSelectionAnswers) {
      setPendingChange(event.target.value)
      setConfirmAnswerSwitchPopupOpen(true)
    } else {
      answer.set(event)
      updateQuestionCandidate(event.target.value)
    }
  }

  const onConfirmAnswerSwitchPopupProceed = () => {
    validate({ silent: true })
    answer.setValue(pendingChange)
    setPendingChange(null)
    setConfirmAnswerSwitchPopupOpen(false)
    setQuestionCandidate({
      ...(questionCandidate || {}),
      answers: [] as QuestionAnswer[],
      answerInputTypeId: pendingChange as string,
    })
  }

  const onAnswerAdded = (newAnswer: QuestionAnswer) => {
    validate({ silent: true })
    setQuestionCandidate({
      ...(questionCandidate || {}),
      answers: (questionCandidate?.answers || []).concat(newAnswer),
    })
    setAddAnswerPopupOpen(false)
  }

  const onAnswerEdited = (newAnswer: QuestionAnswer) => {
    const index =
      answerToEdit && questionCandidate?.answers?.indexOf(answerToEdit)
    const answersToEdit = questionCandidate?.answers || []
    const isIndexValid = index != null && index !== -1
    setQuestionCandidate({
      ...(questionCandidate || {}),
      answers: isIndexValid
        ? R.update(index, newAnswer, answersToEdit)
        : answersToEdit,
    })
    setAnswerToEdit(null)
    setAddAnswerPopupOpen(false)
  }

  const handleDeleteAnswer = (answerToDelete: QuestionAnswer) => {
    setQuestionCandidate({
      ...(questionCandidate || {}),
      answers: (questionCandidate?.answers || []).filter(
        (item) => !R.equals(item, answerToDelete),
      ),
    })
  }

  const handleEditAnswer = (newAnswerToEdit: QuestionAnswer) => {
    setAnswerToEdit(newAnswerToEdit)
    setAddAnswerPopupOpen(true)
  }

  const getCurrentQuestionTypeName = () => {
    const currentType = AnswerInputTypes.find(
      (type) => type.id === answer.value,
    )
    return currentType
      ? LanguageUtils.getTranslatedFieldName(currentType)
      : t('Admin:CATALOG.QUESTION.MULTIPLE_CHOICE')
  }

  const PopperComponent = view ? PuiDialog : PuiPopper

  return (
    <Grid
      container
      className={classes.root}
      direction="column"
      pb={3}
      px={3}
      ref={rootRef}
    >
      <QuestionContentFields
        disabled={Boolean(disabled)}
        permissions={permissions}
        questionSubText={questionSubText}
        questionText={questionText}
      />
      <Grid container item>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <ErrorTooltip
            message={
              isMultipleSelectionType && answersLength < 2
                ? t('Tooltips:EMPTY_ANSWERS_ERROR_MESSAGE', {
                    questionType: getCurrentQuestionTypeName(),
                  })
                : t('Tooltips:CHOOSE_AN_ANSWER_TYPE')
            }
            open={questionText.valid && !answer.valid && !addAnswerPopupOpen}
            placement="top"
          >
            <FormLabel component="legend">
              <TextWithTooltip
                strong
                tooltipText={t('Tooltips:ANSWERS_TYPES')}
                variant="subheading3"
              >
                {t('Common:ANSWER_ONE_OR_OTHER')}*
              </TextWithTooltip>
            </FormLabel>
          </ErrorTooltip>
          <RadioGroup
            aria-label={t('Common:ANSWER_OTHER')}
            name="answers1"
            value={answer.value}
            onChange={onAnswerChange}
          >
            {AnswerInputTypes.map((type) => {
              const isCurrentlySelected = answer.value === type.id

              return (
                <React.Fragment key={type.id}>
                  <FormControlLabel
                    classes={{
                      root: classes.labelRoot,
                      label: classes.radioLabel,
                    }}
                    control={
                      <Radio
                        className={classes.radio}
                        disabled={disableAnswer}
                      />
                    }
                    label={LanguageUtils.getTranslatedFieldName(type)}
                    value={type.id}
                  />
                  {isCurrentlySelected && (
                    <QuestionInput
                      buttonText={t('Common:ADD_ANSWER')}
                      disableAnswer={disableAnswer}
                      handleDeleteAnswer={handleDeleteAnswer}
                      handleEditAnswer={handleEditAnswer}
                      inputType={type.id}
                      question={questionCandidate}
                      setQuestionCandidate={setQuestionCandidate}
                      onAddAnswerRequested={onAddAnswerRequested}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </RadioGroup>
        </FormControl>
      </Grid>
      {!view && (
        <Grid container item alignItems="center" mt={3}>
          <Grid item>
            <Fab
              className={classes.button}
              color="inherit"
              disabled={disabled}
              type="submit"
              variant="extended"
              onClick={next}
            >
              {t('Common:NEXT')}
            </Fab>
          </Grid>
          {showPreview && (
            <Grid item ml={4}>
              <PreviewButton disabled={disabled} onClick={openPreview} />
            </Grid>
          )}
        </Grid>
      )}
      <AnswerPopup
        hideAnswerType
        Component={PuiDialog}
        answer={answerToEdit}
        open={addAnswerPopupOpen}
        onAnswerAdded={onAnswerAdded}
        onAnswerEdited={onAnswerEdited}
        onClose={() => {
          setAddAnswerPopupOpen(false)
          setAnswerToEdit(null)
        }}
      />
      {questionCandidate && (
        <QuestionPreviewContainer
          Component={PopperComponent}
          PopperProps={{
            disableClickAwayListener: true,
            anchorEl: rootRef.current,
          }}
          open={previewPopupOpen}
          question={questionCandidate as Question}
          onClose={() => setPreviewPopupOpen(false)}
        />
      )}
      <ConfirmAnswerSwitchPopup
        Component={PopperComponent}
        PopperProps={{
          anchorEl: rootRef.current,
        }}
        open={confirmAnswerSwitchPopupOpen}
        question={questionCandidate as Question}
        onClose={() => {
          setPendingChange(null)
          setConfirmAnswerSwitchPopupOpen(false)
        }}
        onProceed={onConfirmAnswerSwitchPopupProceed}
      />
      <Shadow
        open={
          addAnswerPopupOpen || previewPopupOpen || confirmAnswerSwitchPopupOpen
        }
      />
    </Grid>
  )
})

export default Question
